import Pusher from 'pusher-js';
import { combineLatest, concat, from, Observable, of, timer } from 'rxjs';
import { distinctUntilChanged, map, mergeMap, publishReplay, refCount, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { get } from 'svelte/store';
import { Analytics, pevent } from './analytics';
import { clearUserInfo, setUserInfo } from './Components/sendbird';
import { EventState } from './Components/shared';
import * as config from './config.json';
import { createLiveChannel } from './live';
import { createPersistedStore } from './persistedStore.js';
import { getPassportRank, getSchedule } from './services';
import { minuteOnMinuteInterval } from './util';

export const BadgesEnabled = config.sections.some(s => s.component === 'badges');

export let pusher = new Pusher('68156377b925b23c8727', {
  cluster: 'us2',
});

const StartingSoonDistance = 60_000 * 10;
function updateEventState(events) {
  const now = new Date();
  return events.map(e => {
    let state;
    if (now < e.start - StartingSoonDistance) state = EventState.Future;
    else if (now < e.start) state = EventState.StartingSoon;
    else if (now < e.end) state = EventState.Live;
    else state = e.replayStream ? EventState.Replay : EventState.Past;
    return {...e, state}
  })
}

export const user = createPersistedStore('user');

// Event schedule store source
const minToMs = min => min * 60_000;
const refetchScheduleEvery = minToMs(20);

// User stream
export const user$ = new Observable(subscriber => user.subscribe(usr => subscriber.next(usr))).pipe(shareReplay(1));
export const userLogin$ = user$.pipe(distinctUntilChanged((p, c) => !!p === !!c), shareReplay(1));

export let liveChannel = createLiveChannel();

if (BadgesEnabled) {
  let sub;
  userLogin$.subscribe(async (usr) => {
    if (usr) {
      sub = liveChannel.subscribe('user_' + usr.id, usr.token, msg => {
        if (msg.event === 'badge_progress') {
          console.log("Progress", msg);
          user.set({...get(user), points: msg.points, rank: msg.rank});
        }
      });

      const rank = await getPassportRank();
      user.set({...get(user), points: rank.points});
    } else {
      sub?.();
    }
  });
}

user$.subscribe(user => {
  if (user) {
    Analytics.setToken(user.token);
    pevent('user', 'login', user.id, 1);
    setUserInfo(user.id, user.name);
  } else {
    Analytics.setToken(undefined);
    clearUserInfo();
  }
});

// Schedule stream: either [], or schedule [] refreshed every `refetchScheduleEvery`
// Events are sorted by startTime
const events$ = user$.pipe(
  startWith(undefined),
  switchMap(u => u ? 
    timer(0, refetchScheduleEvery).pipe(
      mergeMap(_ => from(getSchedule())),
    ) :
    of([])
  ),
  shareReplay(1)
);

// Schedule stream where event state is refreshed every minute (shutsdown when no-one is listening)
// TODO add delay and update api
export const schedule = combineLatest([events$, concat(of(0), minuteOnMinuteInterval())]).pipe(
  map(([schedule, _]) => schedule),
  map(updateEventState),
  publishReplay(1),
  refCount()
);

export function loginUser(id, token, email = '', first = '', last = '') {
  const name = (first + ' ' + last).trim();
  user.set({id, token, email, first, last, name })
  
  const redir = localStorage.getItem('redir');
  if (redir) {
    localStorage.removeItem('redir');
    window.location.href = redir;
  }
}